import React, { useMemo, useState, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ORDERS, ITEMS, INVENTORY } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import GridToolbar from '../components/GridToolbar';
import '../styles/DataGrid.css';

const OrdersPage = () => {
  const gridRef = useRef();
  const { t, fmtCurrency, lang } = useLang();

  const enriched = useMemo(() => {
    return ORDERS.map((o, i) => {
      const item = ITEMS.find(it => it.code === o.itemCode);
      const inv = INVENTORY.find(it => it.code === o.itemCode);
      const atp = inv ? inv.atp : 0;
      return { ...o, _id: i, itemName: item ? item.name : '-', atp,
        shortage: Math.max(0, o.qty - atp), canFulfill: atp >= o.qty,
        unitCost: item ? item.cost : 0, totalCost: item ? o.qty * item.cost : 0 };
    });
  }, []);

  const [rowData, setRowData] = useState(enriched);

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 48, pinned: 'left', lockPosition: true, suppressMenu: true, resizable: false },
    { field: 'id', headerName: t.orders.orderId, width: 120, pinned: 'left', editable: true },
    { field: 'customer', headerName: t.orders.customer, width: 100, editable: true },
    { field: 'itemCode', headerName: t.orders.itemCode, width: 110, editable: true },
    { field: 'itemName', headerName: t.materials.name, width: 200 },
    { field: 'qty', headerName: t.orders.qty, width: 100, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(), valueParser: params => Number(params.newValue) || 0 },
    { field: 'atp', headerName: lang === 'ko' ? '가용재고' : '可用在庫', width: 100, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(),
      cellStyle: params => ({ color: params.value < params.data.qty ? '#dc2626' : '#16a34a', fontWeight: 600 }) },
    { field: 'shortage', headerName: t.orders.shortage, width: 90, type: 'numericColumn',
      valueFormatter: params => params.value > 0 ? params.value.toLocaleString() : '-',
      cellStyle: params => params.value > 0 ? { color: '#dc2626', fontWeight: 700 } : {} },
    { field: 'group', headerName: t.materials.group, width: 100, editable: true },
    { field: 'buyer', headerName: 'Buyer', width: 90, editable: true },
    { field: 'rule', headerName: lang === 'ko' ? '구매규칙' : '購買ルール', width: 100, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['Cost', 'Speed'] },
      cellStyle: params => ({ backgroundColor: params.value === 'Cost' ? '#dbeafe' : '#dcfce7', fontWeight: 600, color: params.value === 'Cost' ? '#2563eb' : '#16a34a' }) },
    { field: 'canFulfill', headerName: lang === 'ko' ? '충족여부' : '充足状況', width: 100,
      valueFormatter: params => params.value ? `✅ ${t.orders.fulfilled}` : `❌ ${t.orders.shortage}`,
      cellStyle: params => ({ fontWeight: 600, color: params.value ? '#16a34a' : '#dc2626' }) },
    { field: 'totalCost', headerName: lang === 'ko' ? '주문금액' : '受注金額', width: 130, type: 'numericColumn',
      valueFormatter: params => fmtCurrency(params.value) },
  ], [t, lang, fmtCurrency]);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  const newRowTemplate = useCallback((data) => ({
    _id: Date.now(), id: `ORD-${1001 + data.length}`, customer: '', itemCode: '', itemName: '-',
    qty: 0, atp: 0, shortage: 0, group: '', buyer: 'Buyer_A', rule: 'Cost', canFulfill: false, unitCost: 0, totalCost: 0,
  }), []);

  const shortage = rowData.filter(r => !r.canFulfill).length;

  return (
    <div className="data-page">
      <div className="data-page-header">
        <div className="data-page-title-area">
          <h2 className="data-page-title">{t.orders.title}</h2>
          <span className="data-page-count">{rowData.length}{t.dashboard.count}</span>
        </div>
        <div className="data-page-badges">
          <span className="badge badge-green">{t.orders.fulfilled}: {rowData.length - shortage}{t.dashboard.count}</span>
          <span className="badge badge-red">{t.orders.unfulfilled}: {shortage}{t.dashboard.count}</span>
        </div>
      </div>
      <GridToolbar gridRef={gridRef} rowData={rowData} setRowData={setRowData} newRowTemplate={newRowTemplate} entityName={lang === 'ko' ? '주문' : '受注'} />
      <div className="ag-theme-alpine data-grid-wrapper">
        <AgGridReact ref={gridRef} rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
          animateRows={true} rowSelection="multiple" enableCellTextSelection={true} undoRedoCellEditing={true}
          stopEditingWhenCellsLoseFocus={true} suppressRowClickSelection={true}
        />
      </div>
    </div>
  );
};

export default OrdersPage;
